import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useApolloClient } from "@apollo/client";
import {
  StyledButton,
  StyledFormGroup,
  StyledInput,
} from "../styledComponents/CommonStyledComponents";
import { StyledLimitWrapper } from "../styledComponents/StyledLimits";
import {
  AccountDescription,
  InvalidFeedback,
} from "../styledComponents/AccountPage";
import { MUTATION_UPDATE_PROFILE } from "../graphql/mutations";

// Displays current Reality Check time and allows picking a new time
// Uses the same styled components as SetRealityCheckDialog/Timeout
const RealityCheck = ({ realityCheck, setSpinner, skin }) => {
  const { t } = useTranslation("");
  const apolloClient = useApolloClient();

  const [currentRealityCheck, setCurrentRealityCheck] = useState(realityCheck);
  // realityCheck prop is in seconds; convert to minutes when present (> 0)
  const initialMinutes = useMemo(() => {
    if (typeof currentRealityCheck === "number" && currentRealityCheck > 0) {
      return Math.floor(currentRealityCheck / 60);
    }
    return 15;
  }, [currentRealityCheck]);

  const [minutes, setMinutes] = useState(initialMinutes);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState("");

  useEffect(() => {
    // keep minutes in sync if prop changes on tab load
    setCurrentRealityCheck(realityCheck);
  }, [realityCheck]);

  useEffect(() => {
    setMinutes(initialMinutes);
  }, [initialMinutes]);

  const handleSubmit = async () => {
    const value = parseInt(minutes, 10);
    if (!value) {
      setFieldError("label_error_reality_check");
      return;
    }
    if (value < 15) {
      setFieldError("label_error_reality_check_min");
      return;
    }
    if (value > 60) {
      setFieldError("label_error_reality_check_max");
      return;
    }

    setFieldError("");
    setSubmitting(true);
    setSpinner && setSpinner(true);
    try {
      const { errors } = await apolloClient.mutate({
        mutation: MUTATION_UPDATE_PROFILE,
        variables: { realityCheck: parseFloat(value * 60) },
        errorPolicy: "all",
      });

      if (errors && errors.length) {
        toast.error(t(errors[0].message), { position: "bottom-center" });
        return;
      }
      setCurrentRealityCheck(value * 60);
      setMinutes(value);
      toast.success(t("update_reality_check_success"), {
        position: "bottom-center",
      });
    } catch (e) {
      toast.error(e.message || "Error", { position: "bottom-center" });
    } finally {
      setSubmitting(false);
      setSpinner && setSpinner(false);
    }
  };

  return (
    <>
      <StyledLimitWrapper selfexcludion>
        <StyledFormGroup margin="m-0" col={"col-md-8"}>
          {typeof currentRealityCheck === "number" &&
            currentRealityCheck > 0 && (
              <label>
                {t("label_reality_check")}{" "}
                {Math.floor(currentRealityCheck / 60)}{" "}
                {t("label_reality_check_minutes")}
              </label>
            )}
          <StyledInput
            inputLg
            type="number"
            min="15"
            step="1"
            max="60"
            value={minutes}
            name="realityCheck"
            onChange={(e) => setMinutes(e.target.value)}
          />
          {fieldError && (
            <InvalidFeedback className="invalid-feedback d-block">
              {t(fieldError)}
            </InvalidFeedback>
          )}
        </StyledFormGroup>
        <StyledButton
          banktransfer
          onClick={handleSubmit}
          pub={skin === "pub"}
          disabled={
            submitting ||
            parseInt(minutes, 10) < 15 ||
            parseInt(minutes, 10) === initialMinutes
          }
        >
          {submitting ? t("label_loading") : t("label_submit")}
        </StyledButton>
      </StyledLimitWrapper>
      <AccountDescription
        timelimit
        expand={true}
        dangerouslySetInnerHTML={{
          __html: t("label_reality_check_description"),
        }}
      ></AccountDescription>
    </>
  );
};

export default RealityCheck;
